/*
 * Estado do Lura Copilot por conversa.
 *
 * Os eventos do backend (SSE) sao aplicados incrementalmente em um "view model" por run:
 *   run = { id, input, status, steps[], streamingText, finalText, error, usage }
 *   step = { kind: 'tool'|'note'|'approval', ... }
 */
import LuraCopilotAPI from 'dashboard/api/luraCopilot';

const subscriptions = new Map(); // conversationId -> unsubscribe()

const AUTO_APPROVE_KEY = conversationId =>
  `lura_copilot_auto_approve_${conversationId}`;

const readAutoApprove = conversationId => {
  try {
    return (
      window.localStorage.getItem(AUTO_APPROVE_KEY(conversationId)) === '1'
    );
  } catch (e) {
    return false;
  }
};

const writeAutoApprove = (conversationId, value) => {
  try {
    window.localStorage.setItem(
      AUTO_APPROVE_KEY(conversationId),
      value ? '1' : '0'
    );
  } catch (e) {
    // sem localStorage: fica so em memoria
  }
};

const emptyThread = () => ({
  autoApprove: false,
  loaded: false,
  loading: false,
  error: null,
  connected: false,
  lastEventId: 0,
  runs: [],
});

const newRun = (id, input, status = 'queued') => ({
  id,
  input,
  status,
  agentName: null,
  autoApprove: false,
  steps: [],
  streamingText: '',
  finalText: null,
  error: null,
  usage: null,
});

const findRun = (thread, runId) => thread.runs.find(run => run.id === runId);

const findStep = (run, callId) =>
  run.steps.find(step => step.callId === callId && step.kind !== 'note');

const applyEvent = (thread, event) => {
  if (event.id <= thread.lastEventId) return;
  thread.lastEventId = event.id;

  let run = findRun(thread, event.runId);
  if (!run) {
    run = newRun(event.runId, event.data.input || '', 'running');
    thread.runs.push(run);
  }
  const { data } = event;

  switch (event.type) {
    case 'run.started':
      run.status = 'running';
      if (data.input) run.input = data.input;
      if (data.agent) run.agentName = data.agent;
      if (data.autoApprove !== undefined) run.autoApprove = !!data.autoApprove;
      break;
    case 'run.status':
      run.status = data.status || run.status;
      if (data.status === 'awaiting_approval' && Array.isArray(data.pending)) {
        data.pending.forEach(pending => {
          const existing = findStep(run, pending.callId);
          if (existing) {
            existing.kind = 'approval';
            existing.decision = existing.decision || null;
          } else {
            run.steps.push({
              kind: 'approval',
              callId: pending.callId,
              name: pending.tool,
              label: pending.label || pending.tool,
              server: pending.server,
              risk: pending.risk,
              classification: pending.classification,
              args: pending.arguments,
              decision: null,
            });
          }
        });
      }
      break;
    case 'assistant.delta':
      run.streamingText += data.delta || '';
      break;
    case 'assistant.message':
      run.streamingText = '';
      run.steps.push({ kind: 'note', text: data.text });
      break;
    case 'tool.call':
      run.steps.push({
        kind: 'tool',
        callId: data.callId,
        name: data.name,
        label: data.label || data.name,
        server: data.server,
        risk: data.risk,
        classification: data.classification,
        args: data.arguments,
        status: 'running',
        preview: null,
        durationMs: null,
      });
      break;
    case 'tool.output': {
      const step = findStep(run, data.callId);
      if (step) {
        step.status = data.ok === false ? 'error' : 'done';
        step.preview = data.preview;
        step.durationMs = data.durationMs;
      } else {
        run.steps.push({
          kind: 'tool',
          callId: data.callId,
          name: data.name,
          label: data.label || data.name,
          status: data.ok === false ? 'error' : 'done',
          preview: data.preview,
          durationMs: data.durationMs,
          args: null,
        });
      }
      break;
    }
    case 'approval.required': {
      const step = findStep(run, data.callId);
      if (step) {
        step.kind = 'approval';
        step.decision = null;
      } else {
        run.steps.push({
          kind: 'approval',
          callId: data.callId,
          name: data.name,
          label: data.label || data.name,
          server: data.server,
          risk: data.risk,
          classification: data.classification,
          args: data.arguments,
          decision: null,
        });
      }
      break;
    }
    case 'approval.resolved': {
      const step = findStep(run, data.callId);
      if (step) {
        step.decision = data.approved ? 'approved' : 'rejected';
        step.decidedBy = data.by;
      }
      run.status = 'running';
      break;
    }
    case 'run.completed':
      run.status = 'completed';
      run.streamingText = '';
      run.finalText = data.finalOutput || '';
      run.usage = data.usage || null;
      break;
    case 'run.failed':
      run.status = 'failed';
      run.streamingText = '';
      run.error = data.error || 'Falha desconhecida';
      break;
    case 'run.cancelled':
      run.status = 'cancelled';
      run.streamingText = '';
      run.error = data.reason || 'Cancelado';
      break;
    default:
      break;
  }
};

const runFromRecord = record => ({
  ...newRun(record.id, record.inputText, record.status),
  agentName: record.agentName || null,
  autoApprove: !!record.autoApprove,
  finalText: record.finalOutput,
  error: record.error,
  usage: record.usage,
});

const state = {
  threads: {},
};

export const getters = {
  getThread: $state => conversationId =>
    $state.threads[conversationId] || emptyThread(),
  getActiveRun: $state => conversationId => {
    const thread = $state.threads[conversationId];
    if (!thread) return null;
    return (
      thread.runs.find(run =>
        ['queued', 'running', 'awaiting_approval'].includes(run.status)
      ) || null
    );
  },
  getPendingApprovals: $state => conversationId => {
    const thread = $state.threads[conversationId];
    if (!thread) return [];
    return thread.runs
      .filter(run => run.status === 'awaiting_approval')
      .flatMap(run =>
        run.steps
          .filter(step => step.kind === 'approval' && !step.decision)
          .map(step => ({ ...step, runId: run.id }))
      );
  },
};

export const mutations = {
  ENSURE_THREAD($state, conversationId) {
    if (!$state.threads[conversationId]) {
      $state.threads[conversationId] = {
        ...emptyThread(),
        autoApprove: readAutoApprove(conversationId),
      };
    }
  },
  SET_AUTO_APPROVE($state, { conversationId, value }) {
    $state.threads[conversationId].autoApprove = !!value;
    writeAutoApprove(conversationId, !!value);
  },
  SET_LOADING($state, { conversationId, loading }) {
    $state.threads[conversationId].loading = loading;
  },
  SET_ERROR($state, { conversationId, error }) {
    $state.threads[conversationId].error = error;
  },
  SET_CONNECTED($state, { conversationId, connected }) {
    $state.threads[conversationId].connected = connected;
  },
  HYDRATE($state, { conversationId, thread }) {
    const current = $state.threads[conversationId];
    current.runs = thread.runs.map(runFromRecord);
    current.lastEventId = 0;
    // Reaplica os eventos historicos para reconstruir etapas/aprovacoes das runs.
    thread.events.forEach(event => applyEvent(current, event));
    thread.runs.forEach(record => {
      const run = findRun(current, record.id);
      if (run) {
        run.status = record.status;
        if (record.finalOutput) run.finalText = record.finalOutput;
        if (record.error) run.error = record.error;
      }
    });
    current.lastEventId = thread.lastEventId || current.lastEventId;
    current.loaded = true;
  },
  APPLY_EVENT($state, { conversationId, event }) {
    applyEvent($state.threads[conversationId], event);
  },
  ADD_RUN($state, { conversationId, run }) {
    const thread = $state.threads[conversationId];
    if (!findRun(thread, run.id)) thread.runs.push(runFromRecord(run));
  },
  MARK_DECISION($state, { conversationId, runId, callId, decision }) {
    const run = findRun($state.threads[conversationId], runId);
    if (!run) return;
    const step = findStep(run, callId);
    if (step) step.decision = decision;
    run.status = 'running';
  },
  CLEAR($state, conversationId) {
    const { autoApprove } = $state.threads[conversationId] || emptyThread();
    $state.threads[conversationId] = {
      ...emptyThread(),
      autoApprove,
      loaded: true,
    };
  },
};

export const actions = {
  async load({ commit, state: $state }, { accountId, conversationId }) {
    commit('ENSURE_THREAD', conversationId);
    if ($state.threads[conversationId].loading) return;
    commit('SET_LOADING', { conversationId, loading: true });
    commit('SET_ERROR', { conversationId, error: null });
    try {
      const thread = await LuraCopilotAPI.getThread(accountId, conversationId);
      commit('HYDRATE', { conversationId, thread });
    } catch (error) {
      commit('SET_ERROR', { conversationId, error: error.message });
    } finally {
      commit('SET_LOADING', { conversationId, loading: false });
    }
  },

  subscribe({ commit, state: $state }, { accountId, conversationId }) {
    commit('ENSURE_THREAD', conversationId);
    if (subscriptions.has(conversationId)) return;
    const unsubscribe = LuraCopilotAPI.subscribe(accountId, conversationId, {
      after: $state.threads[conversationId].lastEventId,
      onOpen: () =>
        commit('SET_CONNECTED', { conversationId, connected: true }),
      onError: () =>
        commit('SET_CONNECTED', { conversationId, connected: false }),
      onEvent: event => commit('APPLY_EVENT', { conversationId, event }),
    });
    subscriptions.set(conversationId, unsubscribe);
  },

  unsubscribe({ commit }, { conversationId }) {
    const unsubscribe = subscriptions.get(conversationId);
    if (unsubscribe) {
      unsubscribe();
      subscriptions.delete(conversationId);
      commit('ENSURE_THREAD', conversationId);
      commit('SET_CONNECTED', { conversationId, connected: false });
    }
  },

  async send(
    { commit, state: $state },
    { accountId, conversationId, message }
  ) {
    commit('ENSURE_THREAD', conversationId);
    const { autoApprove } = $state.threads[conversationId];
    const { run } = await LuraCopilotAPI.startRun(
      accountId,
      conversationId,
      message,
      {
        autoApprove,
      }
    );
    commit('ADD_RUN', { conversationId, run });
    return run;
  },

  setAutoApprove({ commit }, { conversationId, value }) {
    commit('ENSURE_THREAD', conversationId);
    commit('SET_AUTO_APPROVE', { conversationId, value });
  },

  async approve(
    { commit },
    { conversationId, runId, callId, approved, always }
  ) {
    await LuraCopilotAPI.resolveApprovals(runId, [
      { callId, approved, always: !!always },
    ]);
    commit('MARK_DECISION', {
      conversationId,
      runId,
      callId,
      decision: approved ? 'approved' : 'rejected',
    });
  },

  async cancel(_, { runId }) {
    await LuraCopilotAPI.cancelRun(runId);
  },

  async reset({ commit, dispatch }, { accountId, conversationId }) {
    await LuraCopilotAPI.resetThread(accountId, conversationId);
    commit('CLEAR', conversationId);
    dispatch('unsubscribe', { conversationId });
    dispatch('subscribe', { accountId, conversationId });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
